import { Address, SEAT_CUPRA } from '@components/ui/address/types';
import { Button, Card, CardActions, CardContent, Chip, makeStyles, Typography } from '@material-ui/core';
import { isFunction, isNil } from 'lodash';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useRole from 'src/hooks/useRole';
import { CountryCode } from 'src/modules/generated/api';
import UserRole from 'src/types/UserRoles';

import { AddressDrawer } from './AddressDrawer';

type AddressCardProps = Address & {
  title: string;
  onEdit?: (newAddress: Address) => void;
  onDelete?: () => void;
  onSetDefault?: () => void;
  isGlobal?: boolean;
  /**
   * Additional content, rendered below the address.
   */
  content?: ReactNode;
};

const useStyles = makeStyles((theme) => ({
  root: {
    // Forcing cards with same height leads to action bar not sticking to the bottom
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  name: {
    fontWeight: 'bold',
  },
  actions: {
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  action: {
    // fix precedence
    '&&': {
      margin: 0, // handle spacing via gap
    },
  },
}));

export const AddressCard = ({
  title,
  name,
  companyName,
  street,
  city,
  zip,
  country,
  countryCode,
  email,
  phoneNumber,
  faxNumber,
  additionalInformation,
  betriebsId,
  addressCode,
  brandOverrides,
  isGlobal,
  content,
  onEdit,
  onDelete,
  onSetDefault,
}: AddressCardProps): JSX.Element => {
  const classes = useStyles();
  const { hasRole } = useRole();
  const { t } = useTranslation();
  const isAdmin = hasRole(UserRole.ADMIN);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const seatCupra = brandOverrides?.[SEAT_CUPRA];
  // Same rule as the form: Spanish addresses always have a Seat/Cupra pair, even if it is still empty
  const showSeatCupra = countryCode === CountryCode.Es || !!seatCupra;

  const handleSubmitDrawer = (newAddress: Address) => {
    onEdit?.(newAddress);
    setDrawerOpen(false);
  };

  return (
    <>
      <Card className={classes.root}>
        <CardContent>
          <Typography className={classes.name} gutterBottom component="div">
            {name} {isGlobal && <Chip label={t('common.default')} size="small" color="primary" />}
          </Typography>
          <Typography variant="body2" gutterBottom>
            {companyName}
          </Typography>
          <Typography variant="body2" gutterBottom>
            {t('contact.betriebsId')}: {betriebsId || '-'}
          </Typography>
          <Typography variant="body2" gutterBottom>
            {t('contact.addressCode')}: {addressCode || '-'}
          </Typography>
          {showSeatCupra && (
            <>
              <Typography variant="body2" gutterBottom>
                {t('contact.betriebsIdSeatCupra')}: {seatCupra?.betriebsId || '-'}
              </Typography>
              <Typography variant="body2" gutterBottom>
                {t('contact.addressCodeSeatCupra')}: {seatCupra?.addressCode || '-'}
              </Typography>
            </>
          )}
          <Typography variant="body2" gutterBottom>
            {street}
          </Typography>
          <Typography variant="body2" gutterBottom>
            {city && zip && `${city},${zip}`}
          </Typography>
          <Typography variant="body2" gutterBottom>
            {[country, countryCode].filter((c) => c !== undefined).join('•')}
          </Typography>
          <Typography variant="body2" gutterBottom>
            {email && `${t('contact.E-Mail')}:`} {email}
          </Typography>
          <Typography variant="body2" gutterBottom>
            {email && `${t('contact.phone')}:`} {phoneNumber}
          </Typography>
          {!isNil(faxNumber) && (
            <Typography variant="body2">
              {t('contact.fax')}: {faxNumber || '-'}
            </Typography>
          )}
          {!isNil(additionalInformation) && (
            <Typography variant="body2">
              {t('contact.additionalInformation')}: {additionalInformation || '-'}
            </Typography>
          )}
          {content}
        </CardContent>
        <CardActions className={classes.actions}>
          {isFunction(onEdit) && isAdmin && (
            <Button
              className={classes.action}
              variant="outlined"
              color="secondary"
              size="small"
              onClick={() => setDrawerOpen(true)}
            >
              {t('action.edit')}
            </Button>
          )}
          {isFunction(onDelete) && isAdmin && (
            <Button className={classes.action} variant="outlined" color="secondary" size="small" onClick={onDelete}>
              {t('action.delete')}
            </Button>
          )}
          {!isGlobal && isFunction(onSetDefault) && (
            <Button className={classes.action} variant="outlined" color="secondary" size="small" onClick={onSetDefault}>
              {t('addressCard.actions.onSetDefault')}
            </Button>
          )}
        </CardActions>
      </Card>

      {isFunction(onEdit) && (
        <AddressDrawer
          title={title}
          open={drawerOpen}
          onSubmit={handleSubmitDrawer}
          onClose={() => setDrawerOpen(false)}
          address={{
            name,
            companyName,
            street,
            city,
            zip,
            country,
            countryCode,
            email,
            phoneNumber,
            faxNumber,
            additionalInformation,
            betriebsId,
            addressCode,
            brandOverrides,
          }}
        />
      )}
    </>
  );
};
